import { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import { InputAdornment, Skeleton, TextField } from '@mui/material';
import { Tooltip, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Add, Remove } from '@mui/icons-material';
import { getLocaleFromData } from '@ameroservices-platform/attraction-frontend/app/utilities';
import { useTranslation } from 'react-i18next';
import firebaseService from '@ameroservices-platform/shared/services/frontendFirebase';

const moneyFormatter = new Intl.NumberFormat('da-DK', {
	style: 'currency',
	currency: 'DKK',
	minimumFractionDigits: 2
});
function ListOfProductsCartProductRow({
	product,
	productGroup,
	amount,
	contentElementProps,
	onAdd,
	onRemove,
	onSet,
	groupAmount,
	setGroupAmount
}) {
	const flags = useFlags();
	const { t } = useTranslation();
	const [subscriptionType, setSubscriptionType] = useState(null);

	useEffect(() => {
		if (!flags.groupSubscriptions) {
			return;
		}
		const unsubFunc = firebaseService
			.getOrganisationRootDB()
			.collection('subscriptionTypes')
			.where('productUid', '==', product.id)
			.onSnapshot(query => {
				setSubscriptionType(query.docs.map(doc => ({ ...doc.data(), id: doc.id }))[0]);
			});
		return () => {
			unsubFunc();
		};
	}, [product.id, flags.groupSubscriptions]);

	function handleAdd(product) {
		onAdd(product);
	}

	function handleRemove(product) {
		onRemove(product);
	}
	const toolTipTitleAdd = useMemo(() => {
		if (
			flags.maxAmountOfProductPerProductGroupInCart &&
			productGroup &&
			productGroup.maxTickets &&
			productGroup.maxTickets.amount &&
			amount >= productGroup.maxTickets.amount
		) {
			return `${t('YOU_CAN_MAX_BUY')} ${productGroup.maxTickets.amount} ${t('OF_THIS_PRODUCT')}`;
		}
		return '';
	}, [flags.maxAmountOfProductPerProductGroupInCart, productGroup, amount, t]);

	const toolTipTitleRemove = useMemo(() => {
		if (flags.minimumAmountOfProduct && product && product.minimumAmount) {
			return `${t('NEED_TO_BUY_MINIMUM_OF')} ${product.minimumAmount} ${t('OF_THIS_PRODUCT')}`;
		}
		return '';
	}, [flags.minimumAmountOfProduct, product, t]);

	const isGroupAmountEnabled = useMemo(
		() =>
			flags.groupSubscriptions &&
			Object.values(subscriptionType?.selectedProducts ?? {}).some(
				selectedProduct => selectedProduct?.enableGroupAmount
			),
		[subscriptionType, flags.groupSubscriptions]
	);

	return (
		<div className="flex items-center justify-between">
			<Typography className={contentElementProps.productTailwindClasses}>
				{getLocaleFromData(product, 'name')}
			</Typography>
			<div className={'flex items-center'}>
				<div className="flex items-center mr-20">
					{flags.groupSubscriptions && isGroupAmountEnabled && (
						<TextField
							label={t('CART_GROUP_AMOUNT')}
							className={'min-w-150 mr-10'}
							id={'groupAmount-' + product.id}
							value={groupAmount}
							onChange={e => {
								const value = e.target.value.replace(/\D/g, '');
								setGroupAmount(value);
								if (isGroupAmountEnabled && e.target.value === '') {
									onSet(product, 0);
								}
							}}
							type={'number'}
							InputProps={{
								endAdornment: (
									<InputAdornment
										position="end"
										onClick={() => {
											document.getElementById('groupAmount-' + product.id).focus();
										}}
									>
										{t('CART_PERSONS')}
									</InputAdornment>
								)
							}}
							size={'small'}
							error={
								groupAmount !== '' &&
								(Number.isNaN(Number.parseInt(groupAmount, 10)) || groupAmount < 1)
							}
							helperText={
								groupAmount !== '' &&
								(Number.isNaN(Number.parseInt(groupAmount, 10)) || groupAmount < 1) &&
								t('PLEASE_ENTER_CORRECT_NUMBER')
							}
						/>
					)}
					<Typography className="mr-10 font-bold whitespace-normal cursor-default">
						{product && flags.groupSubscriptions && (
							<>
								{product.currentPrice % 1 === 0 && (
									<>{product.currentPrice * (isGroupAmountEnabled ? groupAmount || 1 : 1)},-</>
								)}
								{product.currentPrice % 1 !== 0 && (
									<>
										{moneyFormatter.format(
											product.currentPrice * (isGroupAmountEnabled ? groupAmount || 1 : 1)
										)}
									</>
								)}
							</>
						)}
						{product && !flags.groupSubscriptions && (
							<>
								{product.currentPrice % 1 === 0 && <>{product.currentPrice},-</>}
								{product.currentPrice % 1 !== 0 && <>{moneyFormatter.format(product.currentPrice)}</>}
							</>
						)}
						{!product && <Skeleton />}
					</Typography>
					<div className="flex h-56 items-center">
						<Tooltip title={toolTipTitleRemove}>
							<Button
								variant="outlined"
								disabled={!product}
								onClick={() => handleRemove(product)}
								className="w-32 h-32 md:w-20 md:h-20 lg:w-32 lg:h-32 p-0 min-w-0 rounded-none"
								aria-label={t('ARIA_REMOVE_PRODUCT_FROM_CART')}
							>
								<Remove />
							</Button>
						</Tooltip>
						<div className="flex items-center text-14 font-bold px-10">{amount || '0'}</div>

						<Tooltip title={toolTipTitleAdd}>
							<Button
								variant="outlined"
								disabled={
									!product || (flags.groupSubscriptions && isGroupAmountEnabled && groupAmount < 1)
								}
								onClick={() => handleAdd(product)}
								className="w-32 h-32 md:w-20 md:h-20 lg:w-32 lg:h-32  p-0 min-w-0 rounded-none"
								aria-label={t('ARIA_ADD_PRODUCT_TO_CART')}
							>
								<Add />
							</Button>
						</Tooltip>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ListOfProductsCartProductRow;
